import { API_ROOT, ApiError, http, type Fetch } from './http';
import { SignInRequired } from './savedDesigns';

/**
 * Price requests: what the shop prices by hand, and the quotes it sends back.
 *
 * <p>Made-to-measure cabinets are sold by quote, because how complex one is
 * cannot be judged automatically (owner, 2026-09-28). The shopper sends what
 * they built; the shop prices each item in the admin and sends a quote; the
 * shopper sees it in My designs and accepts it into their basket. There is no
 * mail on this estate, so My designs is where the answer is shown.
 *
 * <p>Always the signed-in shopper's own: the request belongs to their account,
 * and a guest is answered 401, which this port turns into
 * {@link SignInRequired} so the page can send them to sign in and back.
 */

export interface PriceRequestItemInput {
  /** What a person at the shop reads to price it. */
  description: string;
  /** A whole number of pieces, as a decimal string. */
  quantity: string;
  /** The builder's own spec for the item, kept verbatim on the quote line. */
  configuration: Record<string, unknown>;
}

export interface PriceRequestInput {
  /** Which builder sent it: `cabinets`. */
  topic: string;
  title: string;
  summary?: string;
  message?: string;
  phone?: string;
  items: PriceRequestItemInput[];
}

/** The latest quote the shop has sent for a request. Money is decimal strings, as the server sent it. */
export interface PriceRequestQuote {
  quoteNumber: string;
  revision: number;
  /** `PENDING` (sent), `APPROVED` (accepted), `COMPLETED` (ordered), `DECLINED`, `EXPIRED`. */
  status: string;
  currency: string;
  subtotal: string | null;
  taxTotal: string | null;
  total: string | null;
  sentAt: string | null;
  expiresAt: string | null;
  /** Whether it can be accepted now: sent, undecided, and not past its date. */
  acceptable: boolean;
}

export interface PriceRequest {
  id: number;
  topic: string | null;
  title: string | null;
  summary: string | null;
  /** The request's own: `PENDING`, `PROCESSING`, `COMPLETED`, `CANCELLED`. */
  status: string | null;
  createdAt: string | null;
  items: Array<{ description: string; quantity: string }>;
  quote: PriceRequestQuote | null;
}

export interface PriceRequests {
  request(input: PriceRequestInput): Promise<PriceRequest>;
  list(): Promise<PriceRequest[]>;
  /** Accepts a quote; resolves to the key of the basket it became. */
  accept(quoteNumber: string): Promise<string>;
}

export const PRICE_REQUESTS_PATH = `${API_ROOT}/price-requests`;

function signIn(e: unknown): never {
  if (e instanceof ApiError && e.status === 401) throw new SignInRequired();
  throw e;
}

export function accountPriceRequests(fetchImpl?: Fetch): PriceRequests {
  const api = http(fetchImpl);
  return {
    async request(input) {
      return api.send<PriceRequest>('POST', PRICE_REQUESTS_PATH, input).catch(signIn);
    },
    async list() {
      return api.get<PriceRequest[]>(PRICE_REQUESTS_PATH).catch(signIn);
    },
    async accept(quoteNumber) {
      const basket = await api
        .send<{ token?: string | null }>('POST', `${API_ROOT}/quotes/${encodeURIComponent(quoteNumber)}/accept`)
        .catch(signIn);
      if (!basket?.token) throw new Error('The shop accepted the quote but did not say which basket it is in.');
      return basket.token;
    },
  };
}

export type PriceRequestTone = 'waiting' | 'quoted' | 'done' | 'closed';

export interface PriceRequestState {
  label: string;
  tone: PriceRequestTone;
  canAccept: boolean;
}

/** An amount the server sent, shown in its currency. Displayed only, never added up here. */
export function formatAmount(amount: string | null, currency: string): string {
  const value = amount === null ? Number.NaN : Number(amount);
  if (!Number.isFinite(value)) return '';
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value);
  } catch {
    return `${currency} ${amount}`;
  }
}

function formatDay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }).format(d);
}

/**
 * What a request's card says: "Price requested" until a quote is sent, then
 * "Quoted £x, valid until …" while it can be accepted, and what became of it
 * after. A quote past its date reads as expired even before the shop's nightly
 * sweep marks it.
 */
export function priceRequestState(r: PriceRequest, now: Date = new Date()): PriceRequestState {
  const q = r.quote;
  if (!q) {
    return r.status === 'CANCELLED' || r.status === 'COMPLETED'
      ? { label: 'Closed', tone: 'closed', canAccept: false }
      : { label: 'Price requested', tone: 'waiting', canAccept: false };
  }
  const total = formatAmount(q.total, q.currency);
  const expired = q.status === 'EXPIRED' || (q.status === 'PENDING' && q.expiresAt !== null && new Date(q.expiresAt).getTime() <= now.getTime());
  if (q.status === 'PENDING' && !expired) {
    const until = formatDay(q.expiresAt);
    return { label: `Quoted ${total}${until ? `, valid until ${until}` : ''}`, tone: 'quoted', canAccept: q.acceptable };
  }
  if (q.status === 'APPROVED') return { label: `Accepted, ${total} in your basket`, tone: 'done', canAccept: false };
  if (q.status === 'COMPLETED') return { label: `Ordered, ${total}`, tone: 'done', canAccept: false };
  if (q.status === 'DECLINED') return { label: 'Quote declined', tone: 'closed', canAccept: false };
  return { label: 'Quote expired: ask us to price it again', tone: 'closed', canAccept: false };
}
